import { PreRevenueIcon, PreSeedIcon, SeedBeyondIcon } from '@sketchnote/icons';
import { SVGComponentProps } from '@sketchnote/icons';
import { ProgressBar } from '@sketchnote/shared-components';
import classNames from 'classnames';
import * as React from 'react';

import { calculatePercentage } from '../../shared/utils';
import { Image } from '../image';
import { Tag, TagDataType } from '../tags';
import styles from './style.module.css';

export interface ImageTagProps
  extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  src: string;
  tag?: TagDataType;
  alt?: string;
}

export const ImageTag = ({
  src = 'https://firebasestorage.googleapis.com/v0/b/user-login-test-c2254.appspot.com/o/how-to-choose-the-right-pr-for-your-startup-10..png?alt=media&token=8cbb7a7a-27e5-473c-8f93-1a3b2680cb58',
  tag,
  className,
  alt,
  ...otherProps
}: ImageTagProps): JSX.Element => (
  <div
    className={classNames({
      'border-b border-gray-200 p-16 pb-0 overflow-hidden relative w-304': true,
      [className || '']: !!className,
    })}
    style={{ height: '144px' }}
    {...otherProps}
  >
    {tag && tag.label && (
      <Tag className="absolute" color={tag.color}>
        {tag.label}
      </Tag>
    )}
    <Image
      src={src}
      className="m-auto max-w-full max-h-full transition-all duration-500"
      alt={alt}
    />
  </div>
);

export type WrapperProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

export const Wrapper = ({ children, className, ...otherProps }: WrapperProps): JSX.Element => (
  <div
    className={classNames({
      'border rounded-md border-gray-200 cursor-pointer active:border-primary-500 active:shadow-none bg-white flex flex-col justify-between transition-all duration-500':
        true,
      [styles.wrapper]: true,
      [className || '']: !!className,
    })}
    {...otherProps}
  >
    {children}
  </div>
);

export type TitleProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLHeadingElement>,
  HTMLHeadingElement
>;

export const Title = ({ children, className }: TitleProps): JSX.Element => (
  <h2
    className={classNames({
      [styles.title]: true,
      'text-lg link-title min-h-38': true,
      [className || '']: !!className,
    })}
    style={{ lineHeight: '100%' }}
  >
    {children}
  </h2>
);

export interface ProgressProps
  extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  total: number;
  completed: number;
}

export const Progress = ({
  total,
  completed,
  className,
  ...otherProps
}: ProgressProps): JSX.Element => (
  <div
    className={classNames({ 'flex items-center': true, [className || '']: !!className })}
    {...otherProps}
  >
    <div className="w-full">
      <ProgressBar value={calculatePercentage(total, completed)} />
    </div>
    <span className="font-medium text-sm ml-8">{`${completed}/${total}`}</span>
  </div>
);

export interface IconTextProps
  extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  iconRight: (props: SVGComponentProps) => JSX.Element;
}

export const IconText = ({ iconRight: Icon, children, className }: IconTextProps): JSX.Element => (
  <div className={classNames({ 'flex items-center': true, [className || '']: !!className })}>
    <span className="font-medium text-sm mr-8">{children}</span>
    <Icon className="text-green-600" />
  </div>
);

export interface StageProps
  extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  label: string;
  type: 'pre-revenue' | 'pre-seed' | 'seed-beyond';
}

const StageIcon = {
  'pre-revenue': PreRevenueIcon,
  'pre-seed': PreSeedIcon,
  'seed-beyond': SeedBeyondIcon,
};

export const Stage = ({ label, type, className, ...otherProps }: StageProps): JSX.Element => {
  const Icon = StageIcon[type] || PreRevenueIcon;
  return (
    <div
      className={classNames({ 'inline-flex items-center': true, [className || '']: !!className })}
      {...otherProps}
    >
      <span className="mr-6 flex">
        <Icon />
      </span>
      <span className="text-xss font-semibold">{label}</span>
    </div>
  );
};

export type DescriptionProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLParagraphElement>,
  HTMLParagraphElement
>;

export const Description = ({ children, className }: DescriptionProps): JSX.Element => (
  <p
    className={classNames({
      'text-gray-600 text-sm': true,
      [styles.title]: true,
      [className || '']: !!className,
    })}
  >
    {children}
  </p>
);
